import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import api from "@/admin/lib/api";

export type ValueStockStatus = "available" | "in_use" | "retired";

export type ValueStockEntry = {
  object: "attribute_value_stock";
  id: string;
  attribute: string;
  value: string;
  status: ValueStockStatus;
  scan_token?: string;
  scan_url?: string;
  scope?: { entity: string; id: string };
  /** Absent for a scoped reader - which record used a value is not theirs to see. */
  used_by?: { entity: string; id: string };
};

export type ValueStockCounts = {
  available: number;
  in_use: number;
  retired: number;
  total: number;
};

export type ValueStockList = {
  count: number;
  next: string | null;
  previous: string | null;
  counts: ValueStockCounts;
  coverage?: { first: string | null; last: string | null };
  results: ValueStockEntry[];
};

export type ValueStockRejection = {
  value: string;
  reason: "in_use" | "other_scope" | string;
  used_by?: { entity: string; id: string } | null;
  scope?: { entity: string; id: string } | null;
};

export type ValueStockAddResult = {
  added: number;
  skipped: number;
  rejected: ValueStockRejection[];
};

type StockScope = { entity: string; id: string };

const KEY = "value-stock";

function listKey(attribute: string, scope: StockScope | null) {
  return [KEY, attribute, scope?.entity ?? null, scope?.id ?? null];
}

export function useValueStock(params: {
  entity?: string;
  attribute: string;
  scopeEntity: string;
  scopeId: string | null;
  query?: string;
  status?: string;
  page?: number;
  pageSize?: number;
  enabled?: boolean;
}) {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 25;
  const query = params.query?.trim() ?? "";
  const status = params.status?.trim() ?? "";
  return useQuery<ValueStockList>({
    queryKey: [
      ...listKey(params.attribute, {
        entity: params.scopeEntity,
        id: params.scopeId ?? "",
      }),
      query,
      status,
      page,
      pageSize,
    ],
    queryFn: () =>
      api
        .get<ValueStockList>("/value-stock/values/", {
          params: {
            entity: params.entity ?? "holding",
            attribute: params.attribute,
            scope_entity: params.scopeEntity,
            scope_id: params.scopeId,
            query: query || undefined,
            status: status || undefined,
            page,
            page_size: pageSize,
          },
        })
        .then((r) => r.data),
    enabled:
      Boolean(params.attribute) &&
      Boolean(params.scopeId) &&
      params.enabled !== false,
  });
}

// The API caps page_size at 200.
const PRINT_PAGE_SIZE = 200;
export const PRINT_ROW_LIMIT = 2000;

export async function fetchAllValueStock(params: {
  attribute: string;
  scopeEntity: string;
  scopeId: string;
  status?: string;
  query?: string;
}): Promise<ValueStockEntry[]> {
  const rows: ValueStockEntry[] = [];
  let page = 1;
  for (;;) {
    const data = await api
      .get<ValueStockList>("/value-stock/values/", {
        params: {
          entity: "holding",
          attribute: params.attribute,
          scope_entity: params.scopeEntity,
          scope_id: params.scopeId,
          status: params.status || undefined,
          query: params.query?.trim() || undefined,
          page,
          page_size: PRINT_PAGE_SIZE,
        },
      })
      .then((r) => r.data);
    rows.push(...(data.results ?? []));
    if (rows.length > PRINT_ROW_LIMIT) {
      const err = new Error("print-limit");
      err.name = "ValueStockPrintLimit";
      throw err;
    }
    if (!data.next) break;
    page += 1;
  }
  return rows;
}

export function useLoadValueStock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      entity?: string;
      attribute: string;
      scope: StockScope;
      values?: string[];
      range?: { start: string; end: string };
      /**
       * ``abort`` (the default) rejects the whole load when anything collides,
       * because a half-loaded range is a state nobody can reason about.
       */
      on_conflict?: "abort" | "skip";
    }) =>
      api
        .post<ValueStockAddResult>("/value-stock/values/", {
          entity: "holding",
          ...payload,
        })
        .then((r) => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: listKey(vars.attribute, vars.scope) });
    },
  });
}

export function useRetireValueStock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: string;
      attribute: string;
      scope: StockScope;
    }) =>
      api
        .post<ValueStockEntry>(`/value-stock/values/${vars.id}/retire/`)
        .then((r) => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: listKey(vars.attribute, vars.scope) });
    },
  });
}
